import {
  SubHeaderContainer,
  PricingContainer,
  TotalPrice,
  SubHeaderContentWrapper,
  ModelNameLabel,
  AbsolutePricingWrapper,
  LeftSide,
  StyledIcon,
  EditTestConfigLabel,
  RightSide,
  QuantityWrapper,
  QuantityLabel,
  LoaderWrapper,
  LeadTimeWrap,
  LeadTimeLabel,
} from './SubHeader.styled';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { AddEditCartItemModal } from 'components/Modals/AddEditCartItemModal/AddEditCartItemModal';
import { useEffect, useRef, useState } from 'react';
import {
  setCurrentlyEditingShoppingCartLineData,
  triggerAddToShoppingCart,
  triggerEditCartItem,
  triggerUpdateQuotationLine,
} from 'store/ShoppingCart/actions/shoppingCart';
import {
  setIsTestConfigurationModalOpen,
  setTriggerShowSubmitConfirmedModal,
  triggerSubmitConfiguration,
} from 'store/SubmitConfiguration/actions/submitConfiguration';
import { RoutesConfig } from 'navigation/routes';
import {
  resetManuallyChangedQuestionNames,
  resetManuallyClearedQuestionNames,
  resetTouchedQuestionNames,
  setAreAllFieldsTouched,
} from 'store/Form/actions/form';
import { useOutsideAlerter } from 'utils/hooks/useOutsideAlerter';
import {
  IFormattedPricingItem,
  useCalculateAndFormatPrices,
  useIsEditOrAddToShoppingCartRequestInProgress,
  useShouldRoundNumbers,
} from './hooks';
import { useTranslation } from 'react-i18next';
import PricingSection from './PricingSection/PricingSection';
import { useShouldShowSpecificPrices } from './PricingSection/hooks';
import Tooltip from 'components/Tooltip/Tooltip';
import { getTooltipText } from 'pages/ConfigurePage/ConfigureForm/FormItem/helpers';
import { useNavigateWithQuery } from 'utils/hooks/useNavigateWithQuery';
import { COLORS } from 'assets/styled';
import { Skeleton } from '@mui/material';
import Icon from 'components/Icon/Icon';
import { AppMode } from 'store/Common/reducers/common';
import Button from 'components/Button/Button';
import { shouldShowRedBorder } from './helpers';
import { CaretDown, ClockCountdown, Info } from '@phosphor-icons/react';
import { useGetBuilderEditVersionId } from 'providers/QueryProvider/hooks';
import Input from 'components/Input/Input';
import { useLocation } from 'react-router-dom';
import { useFormatMoney } from 'utils/hooks/useFormatMoney';
import { useIsQuotationMode } from 'utils/hooks/useIsQuotationMode';
import { useQuotationLineResolveParams } from 'utils/hooks/useQuotationLineResolveParams';
import { useTemplateConfigureParams } from 'utils/hooks/useTemplateConfigureParams';
import { triggerSaveTemplate } from 'store/SubmitConfiguration/actions/submitConfiguration';
import TemplateNameModal from 'components/Modals/TemplateNameModal/TemplateNameModal';
import { SubmitConfirmedModal } from 'components/Modals/SubmitConfirmedModal/SubmitConfirmedModal';
import { SubmitBeingCheckedModal } from 'components/Modals/SubmitBeingCheckedModal/SubmitBeingCheckedModal';
import Spinner from 'components/Spinner/Spinner';

interface ISubHeaderProps {
  isExpanded: boolean;
  inOrderFlow?: boolean;
}

const SubHeader = ({ isExpanded, inOrderFlow = false }: ISubHeaderProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const queryParams = new URLSearchParams(location.search);
  const taxRate = queryParams.get('taxRate');

  useEffect(() => {
    setTimeout(() => {
      setShowLoader(false);
    }, 1000);
  }, []);

  const {
    triggerShowAddedToCartModal,
    triggerShowCartItemEditedModal,
    currentlyEditingShoppingCartLineId,
    currentlyEditingShoppingCartLineQuantity,
    currency,
  } = useSelector((state: IRootReducerState) => state.shoppingCartInfo);

  const formatMoney = useFormatMoney(currency);

  const { triggerShowSubmitConfirmedModal, submissionBeingChecked } =
    useSelector((state: IRootReducerState) => state.submitConfigurationInfo);

  const [quantity, setQuantity] = useState<string>(
    (currentlyEditingShoppingCartLineQuantity || 1).toString()
  );
  const [isSubmitConfirmedModalOpen, setIsSubmitConfirmedModalOpen] =
    useState(false);

  useEffect(() => {
    if (
      currentlyEditingShoppingCartLineQuantity !== null &&
      currentlyEditingShoppingCartLineQuantity !== undefined
    ) {
      setQuantity(currentlyEditingShoppingCartLineQuantity.toString());
    } else {
      setQuantity('1');
    }
  }, [currentlyEditingShoppingCartLineQuantity]);

  const [isPricingSectionVisible, setIsPricingSectionVisible] =
    useState<boolean>(false);
  const modelInfo = useSelector((state: IRootReducerState) => state.modelInfo);
  const { isFormValid, areAllFieldsTouched } = useSelector(
    (state: IRootReducerState) => state.formInfo
  );
  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const navigateWithQuery = useNavigateWithQuery();
  const isQuotationMode = useIsQuotationMode();
  // Resolving the indicative answers of an existing quotation line: saves back
  // to the quotation instead of a cart, and neither quantity nor pricing is
  // part of that update.
  const isResolvingQuotationLine = !!useQuotationLineResolveParams();
  // Saving a configuration as a quotation template: neither quantity nor pricing
  // is part of a template, and the action saves to /templates instead of a cart.
  const templateParams = useTemplateConfigureParams();
  const isTemplateMode = !!templateParams;
  const [isTemplateNameModalOpen, setIsTemplateNameModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    // Handle state cleanup when either modal closes
    if (!triggerShowAddedToCartModal && !triggerShowCartItemEditedModal) {
      dispatch(resetTouchedQuestionNames());
      dispatch(resetManuallyChangedQuestionNames());
      dispatch(resetManuallyClearedQuestionNames());
      dispatch(setAreAllFieldsTouched(false));
    }
  }, [triggerShowAddedToCartModal, triggerShowCartItemEditedModal]);

  const handleAddToCart = () => {
    dispatch(triggerAddToShoppingCart(true)); // Handled in useListenForAddToCartTrigger
  };

  useEffect(() => {
    if (triggerShowSubmitConfirmedModal) {
      setIsSubmitConfirmedModalOpen(true);
      dispatch(setTriggerShowSubmitConfirmedModal(false));
    }
  }, [triggerShowSubmitConfirmedModal]);

  const handleUpdateQuotationLine = () => {
    dispatch(triggerUpdateQuotationLine(true)); // Handled in useListenForUpdateQuotationLineTrigger
  };

  const handleEditCartItem = () => {
    dispatch(triggerEditCartItem(true)); // Handled in useListenForEditCartItemTrigger
    if (appMode !== AppMode.SUPPLIER && appMode !== AppMode.BUYER) {
      navigateWithQuery(RoutesConfig.Configure.fullPath); // QueryProvider has useListenForAddToCartTrigger, but it triggers only on ConfigurePage
    }
  };

  // Blueprint integration
  const handleSubmitConfiguration = () => {
    dispatch(triggerSubmitConfiguration(true)); // Handled in useListenForSubmitConfigurationTrigger
    navigateWithQuery(RoutesConfig.Configure.fullPath);
  };

  const queryRequestStatus = useSelector(
    (state: IRootReducerState) => state.modelInfo.queryStatus
  );

  const isTestMode = appMode === AppMode.DEALER_PANEL || taxRate !== null;
  const getButtonLabel = () => {
    if (isTemplateMode) {
      return templateParams.templateId
        ? t('Save Template')
        : t('Create Template');
    }
    if (isTestMode) {
      return 'UNKNOWN';
    }
    if (appMode === AppMode.EXTERNAL) {
      return t('Submit');
    } else {
      if (isResolvingQuotationLine) {
        return t('Save product');
      }
      if (currentlyEditingShoppingCartLineId) {
        return isQuotationMode ? t('Edit quotation item') : t('Edit cart item');
      } else {
        return isQuotationMode ? t('Add to quotation') : t('Add to order');
      }
    }
  };

  const pricingContainerRef = useRef(null);
  useOutsideAlerter(pricingContainerRef, () =>
    setIsPricingSectionVisible(false)
  );

  const [totalNetPrice, totalGrossPrice, formattedPrices, isPricingValid] =
    useCalculateAndFormatPrices(modelInfo?.pricing, Number(quantity));

  // Check if there are any pricing components that would be displayed in the pricing section
  const hasAnyPricingComponents = formattedPrices.some(
    (formattedPrice: IFormattedPricingItem) => {
      const hasGrossValue =
        formattedPrice.grossItem?.value && formattedPrice.grossItem.value !== 0;
      const hasNetValue =
        formattedPrice.netItem?.value && formattedPrice.netItem.value !== 0;
      return hasGrossValue || hasNetValue;
    }
  );

  const isPricingEmpty = !hasAnyPricingComponents;

  const { showNet } = useShouldShowSpecificPrices();
  const shouldRoundNumbers = useShouldRoundNumbers();

  const isPriceUnknown = showNet
    ? totalNetPrice === null
    : totalGrossPrice === null;

  const pricelist = modelInfo?.general?.pricelist;
  const pricelistVersion = modelInfo?.general?.pricelist_version;

  const isEditOrAddToShoppingCartRequestInProgress =
    useIsEditOrAddToShoppingCartRequestInProgress();

  // A template is allowed to be incomplete, and an incomplete configuration does
  // not price — so pricing must not gate saving one.
  const isPricingBlockingSubmit = isTemplateMode ? false : !isPricingValid;

  const editVersionId: number | undefined = useGetBuilderEditVersionId();

  return (
    <SubHeaderContainer
      isExpanded={isExpanded}
      inOrderFlow={inOrderFlow}
      externalMode={appMode === AppMode.EXTERNAL}
    >
      <SubHeaderContentWrapper isExpanded={isExpanded}>
        <LeftSide>
          <ModelNameLabel>
            {modelInfo?.general?.name ? (
              modelInfo.general.name
            ) : !queryRequestStatus.attempt ? (
              <Skeleton width={'300rem'} height={'40rem'} animation="wave" />
            ) : (
              t('Unknown model')
            )}
          </ModelNameLabel>
          {pricelist || pricelistVersion ? (
            <Tooltip
              content={getTooltipText(
                ['Pricelist', 'Pricelist Version'],
                [pricelist, pricelistVersion],
                t
              )}
            >
              <Icon svg={Info} size={20} color={COLORS.GREEN} />
            </Tooltip>
          ) : null}
          {(!!modelInfo.connLeadMpr || !!modelInfo.connLeadTime) && (
            <>
              <Tooltip
                content={
                  `${
                    modelInfo.connLeadTime
                      ? `${t('Lead Time')}: ${modelInfo.connLeadTime} ${
                          modelInfo.connLeadTime === 1 ? t('day') : t('days')
                        }`
                      : ''
                  }` +
                  ` ${
                    modelInfo.connLeadMpr ? `(${modelInfo.connLeadMpr})` : ''
                  }`
                }
              >
                <LeadTimeWrap>
                  <Icon svg={ClockCountdown} color={COLORS.GREEN} size={20} />
                  <LeadTimeLabel>{`${modelInfo.connLeadTime} ${
                    modelInfo.connLeadTime === 1 ? t('day') : t('days')
                  }`}</LeadTimeLabel>
                </LeadTimeWrap>
              </Tooltip>
            </>
          )}
        </LeftSide>
        {/* A template is not priced, so the whole pricing area is dropped:
            no total, no breakdown, and no invalid-dimensions hint — an
            incomplete template is expected and must not look like an error. */}
        {isTemplateMode ? null : (
          <AbsolutePricingWrapper>
            {isPriceUnknown ? (
              <TotalPrice>{t('Unknown price')}</TotalPrice>
            ) : (
              <PricingContainer ref={pricingContainerRef}>
                {showLoader ? (
                  <LoaderWrapper>
                    <Spinner />
                  </LoaderWrapper>
                ) : (
                  <TotalPrice>
                    {showNet
                      ? formatMoney(totalNetPrice)
                      : formatMoney(totalGrossPrice)}
                  </TotalPrice>
                )}
                {isPricingEmpty ? null : (
                  <StyledIcon
                    svg={CaretDown}
                    size={20}
                    color={COLORS.BLACK}
                    onClick={() =>
                      setIsPricingSectionVisible(!isPricingSectionVisible)
                    }
                    isPricingSectionVisible={isPricingSectionVisible}
                  />
                )}
                {isPricingSectionVisible && (
                  <PricingSection
                    formattedPrices={formattedPrices}
                    quantity={Number(quantity)}
                    totalPrice={
                      showNet
                        ? parseFloat(
                            totalNetPrice?.toFixed(
                              shouldRoundNumbers ? 0 : 2
                            ) || '0'
                          )
                        : parseFloat(
                            totalGrossPrice?.toFixed(
                              shouldRoundNumbers ? 0 : 2
                            ) || '0'
                          )
                    }
                  />
                )}
                {isPricingValid ? null : t('Invalid dimensions')}
              </PricingContainer>
            )}
          </AbsolutePricingWrapper>
        )}
        <RightSide>
          {appMode !== AppMode.EXTERNAL &&
            !isTestMode &&
            !isTemplateMode &&
            !isResolvingQuotationLine && (
              <QuantityWrapper>
                <QuantityLabel>{t('Quantity')}:</QuantityLabel>
                <Input
                  key={currentlyEditingShoppingCartLineQuantity ?? ''}
                  label=""
                  placeholder=""
                  value={quantity}
                  width={'90rem'}
                  type="number"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setQuantity(value);
                      return;
                    }
                    const number = Number(value);
                    if (isNaN(number) || number < 0) {
                      return;
                    }
                    setQuantity(value);
                  }}
                  onBlur={() => {
                    let value = parseInt(quantity, 10);
                    if (isNaN(value) || value < 1) value = 1;
                    setQuantity(value.toString());
                  }}
                  onIncrease={() => {
                    let value = parseInt(quantity, 10);
                    if (isNaN(value) || value < 1) value = 1;
                    const newValue = value + 1;
                    setQuantity(newValue.toString());
                  }}
                  onDecrease={() => {
                    let value = parseInt(quantity, 10);
                    if (isNaN(value) || value <= 1) value = 1;
                    else value = value - 1;
                    setQuantity(value.toString());
                  }}
                />
              </QuantityWrapper>
            )}
          {isExpanded ? (
            isTestMode && !editVersionId && !isTemplateMode ? (
              <EditTestConfigLabel
                onClick={() => dispatch(setIsTestConfigurationModalOpen(true))}
              >
                {t('Edit Test Configuration')}
              </EditTestConfigLabel>
            ) : (
              <Button
                buttonStyle={
                  shouldShowRedBorder(
                    isTestMode && !isTemplateMode,
                    areAllFieldsTouched,
                    queryRequestStatus?.attempt,
                    isFormValid
                  )
                    ? { border: `1rem solid ${COLORS.RED}` }
                    : {}
                }
                pwId={'submit-form-button'}
                disabled={
                  isEditOrAddToShoppingCartRequestInProgress ||
                  (isTestMode && !isTemplateMode) ||
                  (areAllFieldsTouched &&
                    (queryRequestStatus.attempt ||
                      !isFormValid ||
                      isPricingBlockingSubmit))
                }
                primary
                width="150rem"
                label={getButtonLabel()}
                onClick={() => {
                  if (isFormValid && !isPricingBlockingSubmit) {
                    if (isTemplateMode) {
                      if (templateParams.templateId) {
                        dispatch(triggerSaveTemplate({ name: null }));
                      } else {
                        setIsTemplateNameModalOpen(true);
                      }
                    } else if (appMode === AppMode.EXTERNAL) {
                      handleSubmitConfiguration();
                    } else if (isResolvingQuotationLine) {
                      handleUpdateQuotationLine();
                    } else {
                      const value = parseInt(quantity, 10);
                      const validQuantity =
                        isNaN(value) || value < 1 ? 1 : value;

                      dispatch(
                        setCurrentlyEditingShoppingCartLineData(
                          currentlyEditingShoppingCartLineId,
                          validQuantity
                        )
                      );

                      // Move user back on Config from the BOM or Specs page
                      if (
                        location.pathname === RoutesConfig.Bom.fullPath ||
                        location.pathname === RoutesConfig.Specs.fullPath
                      ) {
                        navigateWithQuery(RoutesConfig.Configure.fullPath);
                      }

                      if (currentlyEditingShoppingCartLineId) {
                        handleEditCartItem();
                      } else {
                        handleAddToCart();
                      }
                    }
                  } else if (!areAllFieldsTouched) {
                    dispatch(setAreAllFieldsTouched(true));
                  }
                }}
              />
            )
          ) : null}
        </RightSide>
      </SubHeaderContentWrapper>
      <AddEditCartItemModal />
      <SubmitConfirmedModal isOpen={isSubmitConfirmedModalOpen} />
      <SubmitBeingCheckedModal
        isOpen={!!submissionBeingChecked}
        message={submissionBeingChecked?.message ?? null}
      />
      <TemplateNameModal
        isOpen={isTemplateNameModalOpen}
        setIsOpen={setIsTemplateNameModalOpen}
        level="FIRST"
        onSubmit={(name: string) => {
          setIsTemplateNameModalOpen(false);
          dispatch(triggerSaveTemplate({ name })); // useListenForSaveTemplateTrigger
        }}
      />
    </SubHeaderContainer>
  );
};

export default SubHeader;
